import React, { useMemo, useState } from "react";
import { useLocation, Link } from "react-router-dom";

const PLANS = {
  starter: {
    name: "Starter",
    price: "$44/mo",
    summary: "For contractors getting started with a focused service area.",
  },
  pro: {
    name: "Pro",
    price: "$89/mo",
    summary: "For growing contractors who want faster alerts and broader coverage.",
  },
  territory_king: {
    name: "Territory King",
    price: "$143/mo",
    summary: "Premium tier for established contractors seeking market dominance.",
  },
};

export default function ContractorCheckout() {
  const location = useLocation();
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const planId = params.get("plan") || "starter";
  const plan = PLANS[planId] || PLANS.starter;

  const [status, setStatus] = useState("idle"); // idle | submitting | success | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");

    const formData = new FormData(e.target);
    const payload = {
      kind: "contractor",
      planId,
      planName: plan.name,
      planPrice: plan.price,
      company: formData.get("company") || "",
      contactName: formData.get("contactName") || "",
      email: formData.get("email") || "",
      phone: formData.get("phone") || "",
      serviceAreas: formData.get("serviceAreas") || "",
      notes: formData.get("notes") || "",
      source: "contractors_checkout_v1",
    };

    try {
      const res = await fetch("https://api.buildbright.app/admin/proxy/leads?trace=1", {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error("Bad response " + res.status);
      }

      setStatus("success");
      e.target.reset();
    } catch (err) {
      console.error("Contractor checkout error", err);
      setStatus("error");
    }
  };

  const buttonClasses =
    "mt-2 inline-flex w-full items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold " +
    (status === "submitting"
      ? "bg-orange-300 text-slate-950 cursor-wait"
      : "bg-orange-400 text-slate-950 hover:bg-orange-300");

  return (
    <main className="min-h-screen bg-slate-950 text-white">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 pt-16 pb-20">
        <Link
          to="/contractors/pricing"
          className="text-xs text-orange-300 hover:text-orange-200"
        >
          ← Back to contractor plans
        </Link>

        <header className="mt-4 mb-8">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-orange-400/80">
            Contractor checkout (pre-Stripe)
          </p>
          <h1 className="mt-2 text-3xl font-semibold">
            Finish getting started with{" "}
            <span className="text-orange-300">{plan.name}</span>.
          </h1>
          <p className="mt-3 text-sm text-slate-300">
            We&apos;ll review your info, confirm your service areas, and follow
            up with next steps to activate your {plan.name} plan. No charges are
            made on this screen yet.
          </p>

          <div className="mt-4 rounded-xl border border-white/10 bg-black/5 p-4 text-sm text-slate-200">
            <div className="flex items-baseline justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-orange-200">
                  Selected plan
                </p>
                <p className="mt-1 font-semibold">{plan.name}</p>
                <p className="text-xs text-slate-400 mt-1">{plan.summary}</p>
              </div>
              <p className="text-lg font-semibold text-orange-300">
                {plan.price}
              </p>
            </div>
          </div>
        
        <div className="mt-4 rounded-xl border border-amber-400/20 bg-orange-400/5 p-4 text-xs text-slate-200">
          <p className="font-semibold text-orange-200 uppercase tracking-[0.18em]">
            What happens next
          </p>
          <ul className="mt-2 space-y-2">
            <li className="flex items-start gap-2">
              <span className="mt-[2px] text-orange-300">📍</span>
              <span>We review your info and confirm your service areas.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-[2px] text-orange-300">📞</span>
              <span>We schedule a quick onboarding call to talk through lead volume and job types.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-[2px] text-orange-300">⚡</span>
              <span>Once you approve, we turn on your plan and send your Stripe checkout link.</span>
            </li>
          </ul>
        </div>

        </header>

        <form
          onSubmit={handleSubmit}
          className="space-y-4 rounded-2xl border border-white/10 bg-black/5 p-6"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-slate-300">
                Company name
              </label>
              <input
                type="text"
                required
                name="company"
                className="mt-1 w-full rounded-lg border border-white/10 bg-black px-3 py-2 text-sm text-white outline-none focus:border-amber-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-300">
                Contact name
              </label>
              <input
                type="text"
                required
                name="contactName"
                className="mt-1 w-full rounded-lg border border-white/10 bg-black px-3 py-2 text-sm text-white outline-none focus:border-amber-400"
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-slate-300">
                Email
              </label>
              <input
                type="email"
                required
                name="email"
                className="mt-1 w-full rounded-lg border border-white/10 bg-black px-3 py-2 text-sm text-white outline-none focus:border-amber-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-300">
                Phone
              </label>
              <input
                type="tel"
                name="phone"
                className="mt-1 w-full rounded-lg border border-white/10 bg-black px-3 py-2 text-sm text-white outline-none focus:border-amber-400"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-300">
              Service areas (cities / ZIPs)
            </label>
            <input
              type="text"
              name="serviceAreas"
              className="mt-1 w-full rounded-lg border border-white/10 bg-black px-3 py-2 text-sm text-white outline-none focus:border-amber-400"
              placeholder="Example: Los Angeles, Pasadena, 90012, 90026 (rough is fine for now)"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-300">
              Anything else we should know?
            </label>
            <textarea
              rows={4}
              name="notes"
              className="mt-1 w-full rounded-lg border border-white/10 bg-black px-3 py-2 text-sm text-white outline-none focus:border-amber-400"
              placeholder="Crew size, typical jobs, slow days, anything else you want us to know."
            />
          </div>

          <button
            type="submit"
            disabled={status === "submitting"}
            className={buttonClasses}
          >
            {status === "submitting"
              ? "Sending your info..."
              : "Submit and request onboarding call"}
          </button>

          {status === "success" && (
            <p className="mt-2 text-xs text-emerald-400">
              Thanks! We received your info about the {plan.name} plan. We&apos;ll review it and reach out within 1 business day to confirm your service areas and next steps.
            </p>
          )}

          {status === "error" && (
            <p className="mt-2 text-xs text-red-400">
              Something went wrong sending your info. Please email{" "}
              <a
                href="support@buildbright.app"
                className="underline"
              >
                support@buildbright.app
              </a>{" "}
              and we&apos;ll take care of you.
            </p>
          )}
        </form>

        <p className="mt-4 text-[11px] text-zinc-400">
          You&apos;re not charged on this screen. We&apos;ll confirm your details together and only send Stripe checkout links after you approve your plan.
        </p>
      </div>
    </main>
  );
}
